import { Component, OnInit, OnDestroy, ChangeDetectorRef, ElementRef, ViewChild, Output, EventEmitter, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, Observable, of, throwError, Subscription, interval } from 'rxjs';
import { catchError, finalize, switchMap, takeWhile, startWith, exhaustMap, tap } from 'rxjs/operators';

import { MockImportService, ValidationResult, ProcessResponse } from '@app/services/mock-import.service';
import { ImportService } from '@app/services/import.service';
import { environment } from 'environments/environment';
import { BackgroundJobMonitorService } from '@app/services/background-job-monitor.service';
import { ToastService } from '@common/services/toast.service';
import * as Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { HttpClient } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

type ImportType = 'create' | 'update';
type WizardStep = 'upload' | 'import' | 'summary';

// Interface for import progress
interface ImportProgress {
  status: 'processing' | 'completed' | 'failed' | 'idle' | 'cancelled' | 'error';
  processed: number;
  total: number;
  message?: string;
  errorCount?: number;
  successCount?: number;
  reportUrl?: string;
  reportId?: string;
}

interface ImportWizardStep {
  id: WizardStep;
  label: string;
  icon: string;
  command: () => void;
  completed: boolean;
  active: boolean;
  disabled: boolean;
}

interface ImportData {
  file: File | FormData;
  importType: ImportType;
  hasHeaders: boolean;
  delimiter: string;
  totalRecords: number;
  start?: number;
  end?: number;
}


const CHUNK_SIZE = 1000; // Number of records to process in each chunk
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ALLOWED_FILE_TYPES = [
  'text/csv',
  // Some browsers label CSV as this legacy Excel MIME
  'application/vnd.ms-excel',
  // Modern Excel MIME
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];
const USE_MOCK = false; // fallback flag to control mock mode

@Component({
  selector: 'sbpro-import-wizard',
  templateUrl: './import-wizard.component.html',
  styleUrls: ['./import-wizard.component.scss'],
  providers: [MockImportService]
})
export class ImportWizardComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
  @ViewChild('importNoteModal') importNoteModalTpl!: TemplateRef<any>;
  @Output() closeImportModal = new EventEmitter<void>();

  // Wizard state
  currentStep: WizardStep = 'upload';
  steps: ImportWizardStep[] = [
    { 
      id: 'upload',
      label: 'Upload', 
      icon: 'pi pi-upload',
      command: () => this.goToStep('upload'), 
      completed: false, 
      active: true, 
      disabled: false 
    },
    { 
      id: 'import',
      label: 'Import', 
      icon: 'pi pi-database',
      command: () => this.goToStep('import'), 
      completed: false, 
      active: false, 
      disabled: true 
    },
    // { 
    //   id: 'summary',
    //   label: 'Summary', 
    //   icon: 'pi pi-check',
    //   command: () => this.goToStep('summary'), 
    //   completed: false, 
    //   active: false, 
    //   disabled: true 
    // }
  ];

  // Form and state
  importForm: FormGroup;
  isProcessing = false;
  isUploading = false;
  isDragOver = false;
  invalidFileType = false;
  statusMessage = 'Select a file to begin import';
  errors: any[] = [];
  selectedFile: File | null = null;
  filePreview: any[] = [];
  showErrorDetails = false;
  currentProgress: ImportProgress | null = null;
  currentJobId: string | null = null;
  validationResult: ValidationResult | null = null;
  importComplete = false;
  canDismissNote = false;
  totalRecords = 0;
  processedRecords = 0;
  private importSub: Subscription | null = null;
  private destroy$ = new Subject<void>();
  private readonly chunkSize = 100; // Records to process per chunk

  // Auto-detection and preview metadata
  detectedDelimiter: string = ',';
  hasHeadersDetected = true;
  previewHeaders: string[] = [];
  previewRows: string[][] = [];
  fileInfo: { name: string; sizeKB: number; type: string } | null = null;
  // Upload-step counters (CSV only): total rows based on quick line count
  uploadTotalRecords: number = 0;

  // Validation results table state
  validationHeaders: string[] = [];
  validationRecords: Array<{ row: number; status: 'success' | 'failed'; message?: string; data: Record<string, any> }> = [];
  showFailedOnly = false;
  // Pagination state for validation table
  pageIndex = 0; // zero-based page index
  pageSize = 50; // default rows per page
  pageSizeOptions: number[] = [25, 50, 100, 200, 500];

  get displayedValidationRecords() {
    return this.showFailedOnly
      ? this.validationRecords.filter(r => r.status === 'failed')
      : this.validationRecords;
  }

  // Filtered and paginated records for rendering
  get filteredValidationRecords() {
    return this.showFailedOnly
      ? this.validationRecords.filter(r => r.status === 'failed')
      : this.validationRecords;
  }

  get pagedValidationRecords() {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredValidationRecords.slice(start, end);
  }

  // Counts for template (avoid inline arrow functions in template)
  get passedValidationCount(): number {
    return this.validationRecords.reduce((acc, r) => acc + (r.status === 'success' ? 1 : 0), 0);
    // alternatively: this.validationRecords.filter(r => r.status === 'success').length
  }

  get failedValidationCount(): number {
    return this.validationRecords.reduce((acc, r) => acc + (r.status === 'failed' ? 1 : 0), 0);
  }

  get filteredValidationCount(): number {
    return this.showFailedOnly ? this.failedValidationCount : this.validationRecords.length;
  }

  setShowFailedOnly(flag: boolean): void {
    this.showFailedOnly = flag;
    this.pageIndex = 0; // reset to first page on filter change
  }

  onPageChange(event: { page: number; rows: number; first: number }): void {
    this.pageIndex = event.page ?? Math.floor((event.first || 0) / (event.rows || this.pageSize));
    this.pageSize = event.rows ?? this.pageSize;
  }

  trackByRow(index: number, rec: { row: number }): number {
    return rec?.row ?? index;
  }

  // Page range getters for template
  get pageStart(): number {
    return this.filteredValidationCount === 0 ? 0 : (this.pageIndex * this.pageSize) + 1;
  }

  get pageEnd(): number {
    const end = (this.pageIndex + 1) * this.pageSize;
    return end > this.filteredValidationCount ? this.filteredValidationCount : end;
  }

  private importNoteModalRef: NgbModalRef | null = null;

  constructor(
    private fb: FormBuilder,
    private mockImportService: MockImportService,
    private importService: ImportService,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private jobMonitor: BackgroundJobMonitorService,
    private http: HttpClient,
    private modalService: NgbModal
  ) {
    this.importForm = this.fb.group({
      file: [null, [Validators.required]]
    });
  }

  /**
   * Start the import (validation is performed server-side during import)
   */
  startImport(): void {
    const file = this.importForm.get('file')?.value as File | null;

    if (!file) {
      this.notify('error', 'Error', 'No file selected to import.', 6000);
      return;
    }

    this.isProcessing = true;
    this.importComplete = false;
    this.canDismissNote = false;
    this.openImportNoteDialog();
    this.errors = [];
    this.currentJobId = null;
    this.currentProgress = {
      status: 'processing',
      processed: 0,
      total: this.uploadTotalRecords || 0,
      message: 'Starting import...'
    };
    this.statusMessage = 'Starting import...';
    this.processedRecords = 0;
    this.totalRecords = this.uploadTotalRecords || 0;

    if (this.importSub) {
      this.importSub.unsubscribe();
    }

    if (USE_MOCK) {
      // Existing mock flow
      const source$ = this.mockImportService.processFile(file, 'create');
      this.importSub = source$.subscribe({
        next: (res: ProcessResponse) => {
          this.canDismissNote = true;
          this.processedRecords = res.processedRecords;
          this.totalRecords = res.totalRecords;
          this.currentProgress = {
            status: res.status === 'completed' ? 'completed' : 'processing',
            processed: res.processedRecords,
            total: res.totalRecords,
            message: res.message,
            errorCount: (res as any)?.errorCount ?? (res.errors?.length || 0),
            reportUrl: (res as any)?.reportUrl
          };
          this.statusMessage = res.message || this.statusMessage;
          this.cdr.detectChanges();
        },
        error: (err: any) => this.handleError(err),
        complete: () => {
          this.canDismissNote = true;
          this.isProcessing = false;
          this.importComplete = true;
          this.notify('success', 'Import Complete', 'Import completed successfully.');
          const summaryStep = this.steps.find(s => s.id === 'summary');
          if (summaryStep) { summaryStep.completed = true; summaryStep.disabled = false; }
          this.goToStep('summary');
          this.cdr.detectChanges();
        }
      });
      return;
    }

    // Async backend flow: start job then register with global monitor and poll read-only (process=false)
    this.importSub = this.importService.startImport(file).pipe(
      switchMap((data:any) => {
        
        this.statusMessage = 'Job created. Processing...';
        this.currentJobId = data?.data?.jobId ?? null;


        // Refresh active jobs via user_jobs API before polling status

        return this.jobMonitor.refreshJobsFromServer().pipe(
          tap(() => {
            this.canDismissNote = true;
          }),
          catchError(refreshError => {
            console.error('Failed to refresh active jobs after starting import', refreshError);
            this.canDismissNote = true;
            return of(null);
          }),
          switchMap(() =>
            interval(800).pipe(
              startWith(0),
              // read-only status checks; background monitor does the actual processing (process=1)
              exhaustMap(() => this.importService.getImportStatus(data.data.jobId, false)),
              takeWhile((job: any) => job && (job.status === 'queued' || job.status === 'processing' || job.status === 'pending'), true)
              // No additional timeout here: long-running jobs must keep polling until the backend signals completion.
            )
          )
        );
      })
    ).subscribe({
      next: (job: any) => {

        if (job?.jobId) {
          this.currentJobId = job.jobId;
        }
        this.canDismissNote = true;

        const processed = job?.processedRecords ?? 0;
        const total = job?.totalRecords ?? this.totalRecords;
        this.processedRecords = processed;
        this.totalRecords = total;
        const status = job?.status;
        const normalizedStatus: ImportProgress['status'] = status === 'completed'
          ? 'completed'
          : (status === 'error' || status === 'failed' ? 'error' : 'processing');
        this.currentProgress = {
          status: normalizedStatus,
          processed,
          total,
          message: job?.message || '',
          errorCount: job?.errorCount ?? 0,
          successCount: job?.successCount ?? undefined,
          reportUrl: job?.reportUrl ?? undefined,
        };
        if (this.currentProgress.status === 'completed') {
          this.statusMessage = 'Import Completed';
        } else if (this.currentProgress.message) {
          this.statusMessage = this.currentProgress.message;
        }
        this.cdr.detectChanges();
      },
      error: (err: any) => {
        this.handleError(err);
        this.jobMonitor.refreshJobsFromServer().pipe(catchError(() => of(null))).subscribe();
      },
      complete: () => {
        this.canDismissNote = true;
        this.isProcessing = false;
        const summaryStep = this.steps.find(s => s.id === 'summary');
        if (summaryStep) { summaryStep.completed = true; summaryStep.disabled = false; }
        this.importComplete = this.currentProgress?.status === 'completed';
        // this.notify(
        //   this.importComplete ? 'success' : 'warn',
        //   this.importComplete ? 'Import Complete' : 'Import Finished',
        //   this.currentProgress?.message || (this.importComplete ? 'Import completed successfully.' : 'Import finished with errors.')
        // );

        if(this.currentProgress?.status == 'error'){
        this.resetToUploadStep()
        }else{
        //this.goToStep('summary');
        }
       
        if (this.currentProgress?.status === 'completed') {
          this.statusMessage = 'Import Completed';
        } else if (this.currentProgress?.message) {
          this.statusMessage = this.currentProgress.message;
        }

        this.cdr.detectChanges();
        this.jobMonitor.refreshJobsFromServer().pipe(catchError(() => of(null))).subscribe();
      }
    });
  }

  ngOnInit(): void {
    this.importForm = this.fb.group({
      file: [null, [Validators.required]]
    });
    
    // Initialize the wizard steps
    this.updateWizardSteps();
  }
  
  private updateWizardSteps(): void {
    const currentStepIndex = this.steps.findIndex(step => step.id === this.currentStep);
    
    this.steps = this.steps.map((step, index) => {
      // Enable all steps up to and including the current step
      const isCompleted = step.completed || index < currentStepIndex;
      const isDisabled = index > currentStepIndex && !isCompleted;
      
      return {
        ...step,
        active: step.id === this.currentStep,
        completed: isCompleted,
        disabled: isDisabled
      };
    });
    
    // Trigger change detection
    this.cdr.detectChanges();
  }
  
  goToStep(stepId: WizardStep): void {
    const targetStep = this.steps.find(step => step.id === stepId);
    if (targetStep && !targetStep.disabled) {
      this.currentStep = stepId;
      this.updateWizardSteps();
    }
  }
  
  resetToUploadStep(): void {
    this.clearFile();
    this.currentStep = 'upload';
    this.updateWizardSteps();
    this.dismissImportNoteModal(true);
  }

  goToStepByIndex(index: number): void {
    if (index >= 0 && index < this.steps.length) {
      const step = this.steps[index];
      if (step && !step.disabled) {
        this.currentStep = step.id;
        this.updateWizardSteps();
      }
    }
  }

  nextStep(): void {
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep);
    if (currentIndex < this.steps.length - 1) {
      const next = this.steps[currentIndex + 1];
      // mark current as completed and enable next
      this.steps[currentIndex].completed = true;
      next.disabled = false;
      this.currentStep = next.id;
      this.updateWizardSteps();
    }
  }

  // Helper for template step checks
  isCurrentStep(stepId: WizardStep): boolean {
    return this.currentStep === stepId;
  }

  ngOnDestroy(): void {
    if (this.importSub) {
      this.importSub.unsubscribe();
    }
    this.dismissImportNoteModal(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  closeImportNoteDialog(silent = false): void {
    this.dismissImportNoteModal(silent);
  }

  private openImportNoteDialog(): void {
    if (this.importNoteModalRef || !this.importNoteModalTpl) {
      return;
    }

    this.importNoteModalRef = this.modalService.open(this.importNoteModalTpl, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      windowClass: 'import-note-dialog'
    });
  }

  private dismissImportNoteModal(silent = false): void {
    if (this.importNoteModalRef) {
      this.importNoteModalRef.close();
      this.importNoteModalRef = null;
      if (!silent) {
        this.closeImportModal.emit();
      }
    }
  }

  /**
   * Handle form submission
   */
  onSubmit(): void {
    if (this.importForm.valid) {
      // Move directly to import step
      this.nextStep();
    } else {
      this.notify('warn', 'Validation Error', 'Please fill in all required fields and select a valid file.');
    }
  }

  /**
   * Download example template file (CSV only)
   */
  downloadTemplate(): void {
    this.downloadAssetFile('assets/imports/devices_import_example.csv', 'devices_import_example.csv');
  }

  /**
   * Handle import cancellation
   */
  onCancel(): void {
    this.isProcessing = false;
    this.importComplete = false;
    this.currentProgress = null;
    this.clearFile();
    this.statusMessage = 'Import cancelled';
    this.cdr.detectChanges();
  }

  /**
   * Get keys from an error record for display
   * @param record The error record to extract keys from
   * @returns Array of keys from the record
   */
  getErrorKeys(record: Record<string, unknown>): string[] {
    return record ? Object.keys(record) : [];
  }

  /**
   * Handle file selection
   */
  onFileSelect(event: any): void {
    if (this.isUploading) {
      return;
    }

    const file: File | undefined = event?.files?.[0] || event?.target?.files?.[0];
    if (!file) {
      this.resetFileInput();
      return;
    }

    // Reset state
    this.clearFile();
    this.errors = [];
    this.validationResult = null;
    this.importComplete = false;
    this.currentProgress = null;
    this.invalidFileType = false;
    this.isDragOver = false;

    // Validate file by MIME or extension
    const lower = file.name.toLowerCase();
    const allowedByExt = lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xls');
    if (!ALLOWED_FILE_TYPES.includes(file.type) && !allowedByExt) {
      this.invalidFileType = true;
      this.notify('error', 'Invalid File Type', 'Please upload a valid CSV or Excel (.xls, .xlsx) file.');
      this.resetFileInput();
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      this.notify('error', 'File Too Large', 'File size must be less than 100MB.');
      this.resetFileInput();
      return;
    }

    this.selectedFile = file;
    this.importForm.patchValue({ file });
    this.statusMessage = `Selected file: ${file.name}`;
    this.fileInfo = { name: file.name, sizeKB: +(file.size / 1024).toFixed(2), type: file.type || '' };
    
    // Preview the file (CSV preview only)
    const isCsv = file.type === 'text/csv' || file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.toLowerCase().endsWith('.xlsx') || file.type === 'application/vnd.ms-excel';

    if (isCsv) {
      this.previewCsvFile(file);
    } else if (isExcel) {
      this.previewExcelFile(file);
    } else {
      this.previewHeaders = [];
      this.previewRows = [];
      this.filePreview = [];
      this.uploadTotalRecords = 0;
      this.cdr.detectChanges();
    }

    this.resetFileInput();
  }

  triggerFileInput(): void {
    if (this.isUploading) {
      return;
    }
    this.fileInputRef?.nativeElement?.click();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isUploading) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDragOver = true;
  }

  onDragEnter(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isUploading) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    this.isDragOver = true;
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isUploading) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'none';
      }
      return;
    }
    this.isDragOver = false;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    if (!file) {
      return;
    }

    this.onFileSelect({ target: { files: [file] } });
    event.dataTransfer?.clearData();
  }

  /**
   * Clear the selected file and reset related state
   */
  clearFile(): void {
    this.invalidFileType = false;
    this.isDragOver = false;
    this.resetFileInput();
    this.selectedFile = null;
    this.filePreview = [];
    this.previewHeaders = [];
    this.previewRows = [];
    this.fileInfo = null;
    if (this.importForm) {
      this.importForm.reset({ file: null });
      this.importForm.markAsPristine();
      this.importForm.markAsUntouched();
    }
    // Reset validation table state
    this.validationHeaders = [];
    this.validationRecords = [];
    this.showFailedOnly = false;
    this.pageIndex = 0;
    this.pageSize = 50;
    // Reset steps
    this.steps.forEach((step, index) => {
      if (index > 0) {
        step.completed = false;
        step.disabled = true;
      }
    });
    const uploadStep = this.steps[0];
    if (uploadStep) {
      uploadStep.completed = false;
      uploadStep.disabled = false;
    }

    // Reset progress
    this.processedRecords = 0;
    this.totalRecords = 0;
    this.currentProgress = null;
    this.uploadTotalRecords = 0;
    this.isProcessing = false;
    this.isUploading = false;
    this.importComplete = false;
    this.errors = [];

    this.statusMessage = 'Select a file to begin import';
    this.cdr.detectChanges();
  }

  private resetFileInput(): void {
    const inputRef = this.fileInputRef?.nativeElement;
    if (inputRef) {
      inputRef.value = '';
    }
  }

  /**
   * Preview the first few rows of the file
   */
  private previewCsvFile(file: File): void {
  const reader = new FileReader();

  reader.onload = (e: ProgressEvent<FileReader>) => {
    try {
      const content = e.target?.result as string;
      if (!content) {
        throw new Error('Failed to read file content');
      }

      // Parse CSV using PapaParse
      const parsed = Papa.parse(content, {
        delimiter: "",      // auto-detect
        header: false,      // we’ll handle headers manually
        skipEmptyLines: true
      });

      const rows: string[][] = parsed.data as string[][];

      // First row = headers
      this.previewHeaders = rows[0] || [];

      // Ignore fully blank rows after header
      const dataRows = rows
        .slice(1)
        .filter((row) => this.isNonEmptyRow(row));

      // Next 5 non-empty rows = preview
      this.previewRows = dataRows.slice(0, 5);

      // Count total non-empty rows (excluding header)
      this.uploadTotalRecords = dataRows.length;

    } catch (error) {
      console.error('Error parsing file preview:', error);
      this.notify('error', 'Error', 'Failed to parse file preview.');
    } finally {
      this.cdr.detectChanges();
    }
  };

  reader.onerror = (error: ProgressEvent<FileReader>) => {
    console.error('Error reading file:', error);
    this.notify('error', 'Error', 'Failed to read file content.');
    this.cdr.detectChanges();
  };

  reader.readAsText(file);
}


  private previewExcelFile(file: File): void {
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      try {
        const result = e.target?.result;
        if (!result) {
          throw new Error('Failed to read file content');
        }

        const workbook = XLSX.read(result, { type: 'array' });
        const sheetName = workbook.SheetNames[0];

        if (!sheetName) {
          this.previewHeaders = [];
          this.previewRows = [];
          this.uploadTotalRecords = 0;
          return;
        }

        const worksheet = workbook.Sheets[sheetName];
        const rawRows = XLSX.utils.sheet_to_json<any[]>(worksheet, {
          header: 1,
          defval: '',
          raw: false,
          dateNF: 'yyyy-mm-dd', // ensure Excel serial dates render as ISO-like strings
        }) as any[][];

        if (!rawRows.length) {
          this.previewHeaders = [];
          this.previewRows = [];
          this.uploadTotalRecords = 0;
          return;
        }

        const normalizedRows: string[][] = rawRows.map(row =>
          (row || []).map(cell => this.formatExcelCell(cell))
        );

        this.previewHeaders = normalizedRows[0] || [];
        const dataRows = normalizedRows
          .slice(1)
          .filter((row) => this.isNonEmptyRow(row));
        this.previewRows = dataRows.slice(0, 5);
        this.uploadTotalRecords = dataRows.length;
      } catch (error) {
        console.error('Error parsing Excel preview:', error);
        this.notify('error', 'Error', 'Failed to parse Excel file preview.');
        this.previewHeaders = [];
        this.previewRows = [];
        this.uploadTotalRecords = 0;
      } finally {
        this.cdr.detectChanges();
      }
    };

    reader.onerror = (error: ProgressEvent<FileReader>) => {
      console.error('Error reading Excel file:', error);
      this.notify('error', 'Error', 'Failed to read Excel file content.');
      this.cdr.detectChanges();
    };

  reader.readAsArrayBuffer(file);
  }

  private formatExcelCell(cell: any): string {
    if (cell === null || cell === undefined) {
      return '';
    }
    if (cell instanceof Date) {
      return cell.toISOString().split('T')[0];
    }
    return String(cell);
  }

  private isNonEmptyRow(row: any[] | undefined | null): boolean {
    if (!row || !Array.isArray(row)) {
      return false;
    }
    return row.some((cell) => String(cell ?? '').trim() !== '');
  }
  // Heuristic delimiter detection
  private detectDelimiter(firstLine: string): string {
    const candidates = [',', ';', '\t', '|'];
    let best = ',';
    let bestCount = -1;
    for (const c of candidates) {
      const count = (firstLine.match(new RegExp(`\\${c}`, 'g')) || []).length;
      if (count > bestCount) { best = c; bestCount = count; }
    }
    return best;
  }

  // Heuristic header detection: if most cells contain letters (not purely numeric)
  private detectHasHeaders(cells: string[]): boolean {
    if (!cells || cells.length === 0) return true;
    const alphaCount = cells.filter(v => /[A-Za-z]/.test(v) && !/^\d+(\.\d+)?$/.test(v)).length;
    return alphaCount >= Math.ceil(cells.length / 2);
  }

  /**
   * Handle next button click
   */
  onNext(): void {
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep);
    if (currentIndex < this.steps.length - 1) {
      const nextStep = this.steps[currentIndex + 1];
      if (nextStep && !nextStep.disabled) {
        this.currentStep = nextStep.id;
        this.updateWizardSteps();
      }
    }
  }

  /**
   * Handle previous button click
   */
  onPrevious(): void {
    const currentIndex = this.steps.findIndex(step => step.id === this.currentStep);
    if (currentIndex > 0) {
      const prevStep = this.steps[currentIndex - 1];
      if (prevStep && !prevStep.disabled) {
        this.currentStep = prevStep.id;
        this.updateWizardSteps();
      }
    }
  }

  /**
   * Handle wizard completion
   */
  /**
   * Handle wizard completion
   */
  onComplete(): void {
    this.importComplete = true;
    // const summaryStep = this.steps.find(step => step.id === 'summary');
    // if (summaryStep) {
    //   summaryStep.completed = true;
    // }
    // this.updateWizardSteps();
    
    this.notify('success', 'Import Complete', 'The import process has been completed successfully.', 5000);
  }

  private handleError(error: any): void {
    console.error('Import error:', error);
    this.isProcessing = false;
    this.isUploading = false;
    this.statusMessage = 'Error during import';
    this.canDismissNote = true;

    const errorPayload = error?.error ?? error;
    const directMessage = typeof errorPayload === 'string' ? errorPayload : null;
    const outerDirectMessage = typeof error === 'string' ? error : null;
    const messageFromData = Array.isArray(errorPayload?.data)
      ? errorPayload.data.find((msg: unknown) => typeof msg === 'string') ?? null
      : typeof errorPayload?.data === 'string'
        ? errorPayload.data
        : null;
    const resolvedMessage =
      messageFromData
      || directMessage
      || (typeof errorPayload?.message === 'string' ? errorPayload.message : null)
      || outerDirectMessage
      || (typeof error?.message === 'string' ? error.message : null)
      || 'An unexpected error occurred during import.';

    this.notify('error', 'Error', resolvedMessage, 10000);

    this.cdr.detectChanges();
  }

  /**
   * Update progress of the import process
   * @param progress The current import progress
   */
  private updateProgress(progress: ImportProgress): void {
    this.currentProgress = progress;
    this.statusMessage = progress.message || 'Processing...';
    this.processedRecords = progress.processed;
    this.totalRecords = progress.total;

    if (progress.status === 'completed') {
      this.isProcessing = false;
      this.isUploading = false;
      this.importComplete = true;
      this.notify('success', 'Import Complete', `Successfully processed ${progress.processed} records.`);
    } else if (progress.status === 'error' || progress.status === 'failed') {
      this.isProcessing = false;
      this.isUploading = false;
      this.notify('error', 'Error', progress.message || 'An error occurred during import.', 10000);
    }
  }

  /**
   * Download error report for failed imports
   */
  downloadErrorReport(): void {
    const url = this.currentProgress?.reportUrl;
    const hasErrors = (this.currentProgress?.errorCount ?? 0) > 0;
    if (!url) {
      this.notify('warn', 'Report Unavailable', hasErrors ? 'The error report is not yet available. Please try again shortly.' : 'No errors found.');
      return;
    }
    // Trigger browser download
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => document.body.removeChild(a), 0);
  }

  private notify(
    severity: 'success' | 'error' | 'warn',
    summary: string,
    detail: string,
    life: number = 5000,
  ): void {
    const defaultOptions = {
      autohide: true,
      delay: life,
    };
    const headerClasses =
      severity === 'success'
        ? 'bg-success text-light'
        : severity === 'error'
          ? 'bg-danger text-light'
          : 'bg-warning text-light';

    this.toastService.show(summary, detail, { headerClasses });
  }

  finish(){
    window.location.reload()
  }
  
    exampleCSV() {
      this.downloadTemplate();
    }

  downloadCompletedFile(jobId: string | null = this.currentJobId): void {
    if (!jobId) {
      this.notify('warn', 'Report Unavailable', 'Import job is not ready yet.');
      return;
    }

    this.http.get(`${environment.apiUrl}common/download_report/${jobId}`, {
      responseType: 'blob',
      observe: 'response' // important: gives you headers too
    }).subscribe((res) => {
      const blob = res.body as Blob;

      // Try to get filename from Content-Disposition header
      let filename = 'downloaded-file';
      const contentDisposition = res.headers.get('Content-Disposition');
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }


    exampleExcel() {
      this.downloadAssetFile('assets/imports/devices_import_example.xlsx', 'devices_import_example.xlsx');
    }

    private downloadAssetFile(relativePath: string, fileName: string): void {
      const url = new URL(relativePath, document.baseURI).toString();
      this.http.get(url, { responseType: 'blob' }).subscribe({
        next: (blob) => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => {
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
          }, 0);
        },
        error: () => {
          this.notify('error', 'Template Download Failed', `Could not download ${fileName}.`);
        }
      });
    }

    private applyTextFormatToColumns(
      worksheet: XLSX.WorkSheet,
      headers: string[],
      columnNames: string[],
      rowCount: number
    ): void {
      const headerRowIndex = 0; // json_to_sheet puts headers in row 0
      const startRow = 1; // data starts on row 1
      const endRow = Math.max(startRow, rowCount);

      columnNames.forEach((name) => {
        const colIndex = headers.indexOf(name);
        if (colIndex < 0) {
          return;
        }

        // Force text formatting for data cells in the column
        for (let r = startRow; r <= endRow; r += 1) {
          const address = XLSX.utils.encode_cell({ r, c: colIndex });
          const cell = worksheet[address] || { t: 's', v: '' };
          cell.t = 's';
          cell.z = '@';
          worksheet[address] = cell;
        }

        // Keep header as string
        const headerAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: colIndex });
        if (worksheet[headerAddress]) {
          worksheet[headerAddress].t = 's';
        }
      });
    }

}


